/**
 * Dashboard header title — truncation + reveal (task #439)
 *
 * A very long event name used to grow the header bar and push "Share access",
 * Feedback and the language picker off the edge. The name is capped at 76
 * characters and finished with an ellipsis so the bar can never be squeezed.
 *
 * The full name is reachable two ways: hovering (native tooltip, unchanged) and
 * — added after the team pointed out that hover doesn't exist on a phone —
 * CLICKING the shortened name, which opens a small pop-up with the whole thing.
 * Keyboard users get the same via Enter/Space; Escape or a click elsewhere
 * closes it.
 *
 * Shared by all 7 dashboards so the rule lives in one place.
 */
(function () {
    'use strict';

    const DASHBOARD_TITLE_MAX = 76;

    // Styles ship with the module so all 7 dashboards pick them up without
    // touching each one's stylesheet. Logical properties keep RTL correct.
    const style = document.createElement('style');
    style.textContent = `
        #dashboard-title.is-truncated {
            cursor: pointer;
            text-decoration: underline dotted rgba(0, 0, 0, 0.28);
            text-underline-offset: 3px;
        }
        #dashboard-title.is-truncated:focus-visible {
            outline: 2px solid var(--primary-700, #16243F);
            outline-offset: 2px;
            border-radius: 4px;
        }
        .dashboard-title-popover {
            position: absolute;
            z-index: 2000;
            max-width: min(520px, calc(100vw - 16px));
            background: #fff;
            color: #2C2C2C;
            border: 1px solid rgba(0, 0, 0, 0.12);
            border-radius: 8px;
            box-shadow: 0 6px 24px rgba(0, 0, 0, 0.16);
            padding: 10px 14px;
            font-size: 0.9rem;
            font-weight: 500;
            line-height: 1.4;
            overflow-wrap: anywhere;
        }
    `;
    (document.head || document.documentElement).appendChild(style);

    function capDashboardTitle(text) {
        const s = String(text || '');
        return s.length > DASHBOARD_TITLE_MAX
            ? s.slice(0, DASHBOARD_TITLE_MAX - 1).trimEnd() + '…'
            : s;
    }

    let popover = null;
    let popoverAnchor = null;

    function closePopover() {
        if (popover) {
            popover.remove();
            popover = null;
        }
        popoverAnchor = null;
        document.removeEventListener('click', onDocumentClick, true);
        document.removeEventListener('keydown', onDocumentKeydown, true);
    }

    function onDocumentClick(e) {
        if (!popover) return;
        // Clicks on the title itself are the anchor's own toggle — this
        // capture-phase listener runs first, so closing here would let the
        // anchor's handler immediately reopen it.
        if (popoverAnchor && popoverAnchor.contains(e.target)) return;
        if (!popover.contains(e.target)) closePopover();
    }

    function onDocumentKeydown(e) {
        if (e.key === 'Escape') closePopover();
    }

    function openPopover(anchor, fullText) {
        closePopover();
        popoverAnchor = anchor;
        popover = document.createElement('div');
        popover.className = 'dashboard-title-popover';
        popover.setAttribute('role', 'dialog');
        popover.textContent = fullText;
        document.body.appendChild(popover);

        const rect = anchor.getBoundingClientRect();
        // Keep the pop-up inside the viewport on narrow screens rather than
        // letting it hang off the edge and create a horizontal scrollbar.
        const margin = 8;
        const width = Math.min(popover.offsetWidth, window.innerWidth - margin * 2);
        popover.style.maxWidth = width + 'px';
        let left = rect.left;
        if (left + width > window.innerWidth - margin) left = window.innerWidth - margin - width;
        if (left < margin) left = margin;
        popover.style.left = Math.round(left) + 'px';
        popover.style.top = Math.round(rect.bottom + window.scrollY + 6) + 'px';

        // Bound to the capture phase so the click that opened it doesn't
        // immediately close it again.
        setTimeout(() => {
            document.addEventListener('click', onDocumentClick, true);
            document.addEventListener('keydown', onDocumentKeydown, true);
        }, 0);
    }

    /**
     * Paint the header title element with the (possibly shortened) event name.
     * Safe to call repeatedly — it rewires cleanly each time.
     */
    function applyDashboardTitle(el, fullText) {
        if (!el) return;
        const full = String(fullText || '');
        const shown = capDashboardTitle(full);
        el.textContent = shown;
        el.title = full; // hover tooltip, unchanged

        const truncated = shown !== full;
        el.classList.toggle('is-truncated', truncated);

        if (el._dashboardTitleHandler) {
            el.removeEventListener('click', el._dashboardTitleHandler);
            el.removeEventListener('keydown', el._dashboardTitleKeyHandler);
            el._dashboardTitleHandler = null;
            el._dashboardTitleKeyHandler = null;
        }

        if (!truncated) {
            el.removeAttribute('role');
            el.removeAttribute('tabindex');
            el.removeAttribute('aria-label');
            return;
        }

        el.setAttribute('role', 'button');
        el.setAttribute('tabindex', '0');
        el.setAttribute('aria-label', full);

        const handler = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (popover) closePopover();
            else openPopover(el, full);
        };
        const keyHandler = (e) => {
            if (e.key === 'Enter' || e.key === ' ') handler(e);
        };
        el._dashboardTitleHandler = handler;
        el._dashboardTitleKeyHandler = keyHandler;
        el.addEventListener('click', handler);
        el.addEventListener('keydown', keyHandler);
    }

    window.capDashboardTitle = capDashboardTitle;
    window.applyDashboardTitle = applyDashboardTitle;
})();
